import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { CutoutTier, RenderRow, OverrideRow } from '../api/types';
import { ReprocessDialog } from './ReprocessDialog';
import { LazyImage } from './LazyImage';
import { useSelectionStore } from '../store/selectionStore';
import { useRowsRegistry } from '../store/rowsRegistry';

interface Props {
  storeId: string;
}

const PAGE_SIZE = 24;

function asOverrideRowShim(r: RenderRow): OverrideRow {
  // ReprocessDialog accepts an OverrideRow but in practice only reads
  // row.product_id. We synthesize a minimal compatible shape from the
  // gallery row so we don't have to fork the dialog. The `as unknown as`
  // cast acknowledges that the runtime-required field set is narrower
  // than the type declares.
  return {
    product_id: r.product_id,
    reported_at: r.rendered_at || '',
    reported_by: 'gallery',
    reason: 'manual',
    force_tier: 'tier_1',
    resolved_at: null,
    notes: null,
  } as unknown as OverrideRow;
}

export function RendersGallery({ storeId }: Props) {
  const [offset, setOffset] = useState(0);
  const [tier, setTier] = useState<CutoutTier | ''>('');
  const [maxScore, setMaxScore] = useState<string>('');
  const [sort, setSort] = useState<'rendered_at_desc' | 'rendered_at_asc' | 'score_asc' | 'score_desc' | 'product_id_asc'>('rendered_at_desc');
  const [status, setStatus] = useState<'' | 'ok' | 'cache_hit' | 'needs_review'>('');
  const [isApproved, setIsApproved] = useState<'' | 'true' | 'false'>('');
  const [graded, setGraded] = useState<'' | 'true' | 'false'>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedRow, setSelectedRow] = useState<RenderRow | null>(null);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    topRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [offset]);

  // Debounce so we don't re-query on every keystroke; the committed term
  // resets paging like every other filter's onChange does (server-side paging).
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const toggle = useSelectionStore((s) => s.toggle);
  const range = useSelectionStore((s) => s.range);
  const setLastSelected = useSelectionStore((s) => s.setLastSelected);
  const lastSelectedId = useSelectionStore((s) => s.lastSelectedId);
  const clearSelection = useSelectionStore((s) => s.clear);

  const upsertRow = useRowsRegistry((s) => s.upsert);
  const clearRows = useRowsRegistry((s) => s.clear);

  // Clear shared selection when leaving the gallery so other surfaces don't
  // inherit stale ids.
  useEffect(() => clearSelection, [clearSelection]);

  // Independent cleanup for the rows registry — different store, same intent:
  // don't let stale row metadata survive a sub-tab switch / unmount.
  useEffect(() => clearRows, [clearRows]);

  const q = useQuery({
    queryKey: ['renders', storeId, offset, tier, maxScore, sort, status, isApproved, graded, debouncedSearch],
    queryFn: () => api.listRenders(storeId, {
      limit: PAGE_SIZE,
      offset,
      tier: tier || undefined,
      max_score: maxScore ? parseFloat(maxScore) : undefined,
      sort,
      status: status || undefined,
      is_approved: isApproved === '' ? undefined : isApproved === 'true',
      graded: graded === '' ? undefined : graded === 'true',
      search: debouncedSearch || undefined,
    }),
    placeholderData: (prev) => prev,
  });

  const rows = q.data?.rows ?? [];

  // Mirror visible rows into the shared registry so the Cutout QA tab's
  // bulk-approve mutation can build BulkApprovalItem entries (needs image_link
  // + cutout_tier) from the selected ids without re-fetching.
  useEffect(() => {
    for (const r of rows) {
      upsertRow({
        product_id: r.product_id,
        image_link: r.image_link,
        cutout_tier: r.cutout_tier,
      });
    }
  }, [rows, upsertRow]);

  function handleCardClick(e: React.MouseEvent, row: RenderRow) {
    if (e.shiftKey && lastSelectedId) {
      // Range select using the current rendered order — NOT product_id sort,
      // because the gallery may be sorted by score/date/etc.
      const lastIdx = rows.findIndex((x) => x.product_id === lastSelectedId);
      const thisIdx = rows.findIndex((x) => x.product_id === row.product_id);
      if (lastIdx >= 0 && thisIdx >= 0) {
        const [from, to] = lastIdx <= thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
        range(rows.slice(from, to + 1).map((x) => x.product_id));
        setLastSelected(row.product_id);
        return;
      }
    }
    if (selectedIds.size > 0) {
      e.preventDefault();
      toggle(row.product_id);
      return;
    }
    // Normal click: open the dialog
    setSelectedRow(row);
  }

  return (
    <div ref={topRef}>
      <div className="flex gap-3 mb-4 items-center flex-wrap">
        <select
          aria-label="tier filter"
          value={tier}
          onChange={(e) => { setTier(e.target.value as CutoutTier | ''); setOffset(0); }}
          className="ff-input-sm"
        >
          <option value="">All tiers</option>
          <option value="tier_0">tier_0</option>
          <option value="tier_1">tier_1</option>
          <option value="tier_2">tier_2</option>
          <option value="ben2">ben2</option>
          <option value="tier_sam">tier_sam</option>
          <option value="floodfill">floodfill</option>
          <option value="passthrough">passthrough</option>
          <option value="snapedit">snapedit</option>
          <option value="none">none</option>
        </select>
        <select
          value={maxScore}
          onChange={(e) => { setMaxScore(e.target.value); setOffset(0); }}
          className="ff-input-sm"
        >
          <option value="">Any score</option>
          <option value="0.5">≤ 0.5</option>
          <option value="0.8">≤ 0.8</option>
          <option value="0.95">≤ 0.95</option>
        </select>
        <select
          value={sort}
          onChange={(e) => { setSort(e.target.value as typeof sort); setOffset(0); }}
          className="ff-input-sm"
        >
          <option value="rendered_at_desc">Recent first</option>
          <option value="rendered_at_asc">Oldest first</option>
          <option value="score_asc">Score: low first</option>
          <option value="score_desc">Score: high first</option>
          <option value="product_id_asc">Product ID</option>
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value as typeof status); setOffset(0); }}
          className="ff-input-sm"
        >
          <option value="">Any status</option>
          <option value="ok">OK</option>
          <option value="cache_hit">Cache hit</option>
          <option value="needs_review">Needs review</option>
        </select>
        <select
          value={isApproved}
          onChange={(e) => { setIsApproved(e.target.value as typeof isApproved); setOffset(0); }}
          className="ff-input-sm"
        >
          <option value="">Any approval</option>
          <option value="true">Approved only</option>
          <option value="false">Not approved</option>
        </select>
        <select
          aria-label="graded filter"
          value={graded}
          onChange={(e) => { setGraded(e.target.value as typeof graded); setOffset(0); }}
          className="ff-input-sm"
        >
          <option value="">Any grade</option>
          <option value="true">AI-graded only</option>
          <option value="false">Ungraded only</option>
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search id or title…"
          aria-label="Search by product id or title"
          data-testid="renders-search"
          className="ff-input-sm min-w-44 flex-1 xl:flex-none"
        />
        <span
          data-testid="renders-counts"
          className="font-mono text-[11px] text-ff-mute ml-auto text-right leading-tight"
        >
          {q.data && (
            <>
              <span className="text-ff-forest">{q.data.graded_total.toLocaleString()}</span>
              {' graded · '}
              {(q.data.base_total - q.data.graded_total).toLocaleString()}
              {' ungraded · '}
              {q.data.base_total.toLocaleString()}
              {' total'}
              <span className="block text-ff-mute/70">
                showing {offset + 1}–{offset + q.data.rows.length} of {q.data.total.toLocaleString()}
              </span>
            </>
          )}
        </span>
      </div>

      {q.isLoading && <div className="text-ff-mute italic text-sm">Loading renders…</div>}
      {q.isError && <div className="text-ff-danger text-sm">Couldn't load renders.</div>}

      {q.data && q.data.rows.length === 0 && (
        <div className="text-ff-mute italic text-sm py-12 text-center">
          No renders match your filters. Try widening the date range or clearing the tier filter.
        </div>
      )}

      {q.data && q.data.rows.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {rows.map((r) => {
            const isSelected = selectedIds.has(r.product_id);
            return (
              <div
                key={r.product_id}
                role="button"
                tabIndex={0}
                onClick={(e) => handleCardClick(e, r)}
                onKeyDown={(e) => { if (e.key === 'Enter') setSelectedRow(r); }}
                className={`relative overflow-hidden rounded-2xl border bg-white text-left shadow-soft transition cursor-pointer ${
                  isSelected ? 'border-ff-orange ring-2 ring-ff-orange' : 'border-ff-line hover:border-ff-ink/25 hover:shadow-hover'
                }`}
              >
                <div className="absolute top-2 right-2 z-10 flex flex-col items-end gap-1">
                  {r.is_approved && (
                    <span className="rounded-full bg-ff-forest text-white text-[9px] font-mono px-1.5 py-0.5">
                      {r.approved_by && r.approved_by.startsWith('ui') ? '✓ manually approved' : '✓ approved'}
                    </span>
                  )}
                  {r.is_graded && (
                    <span
                      className={
                        'rounded-full text-[9px] font-mono px-1.5 py-0.5 ' +
                        (r.qa_verdict === 'fail'
                          ? 'bg-ff-danger-soft text-ff-danger'
                          : 'bg-ff-mint text-ff-forest')
                      }
                      title={`AI QA verdict: ${r.qa_verdict ?? 'graded'}`}
                    >
                      {r.qa_verdict === 'fail' ? '⚑ flagged' : '✓ graded'}
                    </span>
                  )}
                </div>
                <input
                  type="checkbox"
                  aria-label={`Select ${r.product_id}`}
                  checked={isSelected}
                  onChange={() => { toggle(r.product_id); setLastSelected(r.product_id); }}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-2 left-2 z-10 accent-ff-orange"
                />
                {r.image_link && (
                  <LazyImage
                    src={r.image_link}
                    alt={r.title || r.product_id}
                    className="w-full aspect-square object-contain bg-ff-paper"
                    wrapperClassName="w-full aspect-square"
                  />
                )}
                <div className="p-2">
                  <div className="font-mono text-[10px] text-ff-mute">{r.product_id}</div>
                  <div className="text-sm truncate">{r.title || '—'}</div>
                  <div className="flex justify-between mt-1 text-[10px] font-mono text-ff-mute">
                    <span>{r.cutout_tier || '—'}</span>
                    <span>{r.cutout_quality_score?.toFixed(2) ?? '—'}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {q.data && q.data.has_more && (
        <div className="text-center mt-6">
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="ff-btn-ghost"
            disabled={q.isFetching}
          >
            {q.isFetching ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {selectedRow && (
        <ReprocessDialog
          storeId={storeId}
          row={asOverrideRowShim(selectedRow)}
          onClose={() => setSelectedRow(null)}
          onResolved={() => { setSelectedRow(null); q.refetch(); }}
        />
      )}
    </div>
  );
}
